import { useState, useEffect } from "react"
import { lumi } from "../lib/lumi"
import { useAuth } from "../hooks/useAuth"
import {FileText, Calendar, ChevronLeft, ChevronRight, Clock, Users, User, Paperclip, Download, Link2} from 'lucide-react'
import { LinkedIssueBadges } from "./LinkedIssueBadges"
import { setPendingIssueTarget, type LinkedIssue } from "../lib/issueLink"

const ACTIVITY_COLORS = {
  "개발/설계": "#3B82F6",
  "영업/견적/PM/기획": "#10B981",
  "제작/조립/구매": "#F59E0B",
  "시험/검증/테스트": "#8B5CF6",
  "설치/납품/AS": "#EF4444",
  "지원/문서/교육/미팅": "#EC4899"
}

export function SubmittedReportsView() {
  const { user } = useAuth()
  const [selectedWeek, setSelectedWeek] = useState<Date>(getMonday(new Date()))
  const [reports, setReports] = useState<any[]>([])
  const [selectedReport, setSelectedReport] = useState<any>(null)
  const [prevWeekReport, setPrevWeekReport] = useState<any>(null)
  const [timeEntries, setTimeEntries] = useState<any[]>([])
  const [attachments, setAttachments] = useState<any[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (user) {
      loadWeeklyReports()
    }
  }, [selectedWeek, user])

  useEffect(() => {
    if (selectedReport) {
      loadTimeEntriesForReport(selectedReport)
      loadAttachmentsForReport(selectedReport)
      loadPreviousWeekReport(selectedReport)
    } else {
      setPrevWeekReport(null)
    }
  }, [selectedReport])

  const loadWeeklyReports = async () => {
    setLoading(true)
    try {
      const weekStartDate = new Date(selectedWeek)
      weekStartDate.setHours(0, 0, 0, 0)
      const weekStart = weekStartDate.toISOString()
      
      const weekEndDate = getWeekEnd(selectedWeek)
      weekEndDate.setHours(0, 0, 0, 0)
      const weekEnd = weekEndDate.toISOString()

      console.log("[SubmittedReportsView] Loading all reports with:", {
        weekStart,
        weekEnd
      })

      // 모든 제출된 보고서 조회 (승인 상태 무관)
      const result = await lumi.entities.weeklyReports.list({
        filter: {
          weekStart,
          weekEnd,
          status: "submitted"
        }
      })

      console.log("[SubmittedReportsView] Total reports fetched:", result.list.length)

      // 제출시간 기준으로 정렬
      const sortedReports = result.list.sort((a: any, b: any) => {
        return new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
      })

      setReports(sortedReports)
      setSelectedReport(null)
      setTimeEntries([])
    } catch (error) {
      console.error("Failed to load weekly reports:", error)
    } finally {
      setLoading(false)
    }
  }

  const loadTimeEntriesForReport = async (report: any) => {
    try {
      const result = await lumi.entities.time_entries2.list({
        filter: {
          userId: report.userId,
          weekStart: report.weekStart,
          status: "submitted"
        }
      })

      setTimeEntries(result.list)
    } catch (error) {
      console.error("Failed to load time entries:", error)
      setTimeEntries([])
    }
  }

  const loadPreviousWeekReport = async (report: any) => {
    try {
      const prevStart = new Date(report.weekStart)
      prevStart.setDate(prevStart.getDate() - 7)
      prevStart.setHours(0, 0, 0, 0)

      const result = await lumi.entities.weeklyReports.list({
        filter: {
          userId: report.userId,
          weekStart: prevStart.toISOString()
        }
      })

      setPrevWeekReport(result.list.length > 0 ? result.list[0] : null)
    } catch (error) {
      console.error("Failed to load previous week report:", error)
      setPrevWeekReport(null)
    }
  }

  const loadAttachmentsForReport = async (report: any) => {
    try {
      const result = await lumi.entities.attachments.list({
        filter: {
          reportId: report._id
        }
      })

      setAttachments(result.list)
    } catch (error) {
      console.error("Failed to load attachments:", error)
      setAttachments([])
    }
  }

  const previousWeek = () => {
    const newDate = new Date(selectedWeek)
    newDate.setDate(newDate.getDate() - 7)
    setSelectedWeek(newDate)
  }

  const nextWeek = () => {
    const newDate = new Date(selectedWeek)
    newDate.setDate(newDate.getDate() + 7)
    setSelectedWeek(newDate)
  }

  const handleNavigateToIssue = (issue: LinkedIssue) => {
    setPendingIssueTarget({ equipmentId: issue.equipmentId, issueId: issue.issueId })
    window.dispatchEvent(new Event("navigateToEquipmentIssue"))
  }

  const getProjectTimeSummary = () => {
    const summary: { [projectName: string]: { [activityCode: string]: number } } = {}

    timeEntries.forEach((entry: any) => {
      if (entry.isVacation) return

      if (!entry.projectName || 
          entry.projectName === "연차" || 
          entry.projectName === "오전반차" || 
          entry.projectName === "오후반차") {
        return
      }

      if (!summary[entry.projectName]) {
        summary[entry.projectName] = {}
      }

      if (entry.dailyEntries) {
        entry.dailyEntries.forEach((daily: any) => {
          if (daily.activities) {
            daily.activities.forEach((activity: any) => {
              const code = activity.activityCode
              const hours = activity.hours || 0
              
              if (!code || hours === 0) return
              
              if (!summary[entry.projectName][code]) {
                summary[entry.projectName][code] = 0
              }
              summary[entry.projectName][code] += hours
            })
          }
        })
      }
    })

    return summary
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50">
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl shadow-lg">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
                제출주간보고 확인
              </h1>
              <p className="text-gray-600 mt-1">전 직원이 제출한 주간보고를 주차별로 확인하세요</p>
            </div>
          </div>
        </div>

        {/* Week Navigation */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <button
              onClick={previousWeek}
              className="p-2 hover:bg-orange-50 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-orange-600" />
            </button>

            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-orange-600" />
              <span className="text-lg font-semibold text-gray-900">
                {formatDate(selectedWeek)} ~ {formatDate(getWeekEnd(selectedWeek))}
              </span>
            </div>

            <button
              onClick={nextWeek}
              className="p-2 hover:bg-orange-50 rounded-lg transition-colors"
            >
              <ChevronRight className="w-6 h-6 text-orange-600" />
            </button>
          </div>
        </div>

        {/* Reports List - 전체 폭, 1줄 2~4명 카드 그리드 */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-orange-600" />
            제출된 보고서 ({reports.length})
          </h2>

          {loading ? (
            <div className="text-center py-8 text-gray-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
              <p className="mt-2">로딩 중...</p>
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p>제출된 보고서가 없습니다</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 max-h-[300px] sm:max-h-[240px] overflow-y-auto pr-1">
              {reports.map((report) => (
                <div
                  key={report._id}
                  onClick={() => setSelectedReport(report)}
                  className={`p-2.5 sm:p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-md ${
                    selectedReport?._id === report._id
                      ? "border-orange-500 bg-orange-50"
                      : "border-gray-200 hover:border-orange-300"
                  }`}
                >
                  <div className="flex items-start justify-between mb-1.5 sm:mb-2">
                    <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                      <User className="w-4 h-4 text-orange-600 flex-shrink-0" />
                      <span className="font-semibold text-gray-900 text-sm sm:text-base truncate">{report.userName}</span>
                    </div>
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 space-y-0.5 sm:space-y-1">
                    <div className="flex items-center gap-1 sm:gap-2 min-w-0">
                      <span className="text-gray-500 hidden sm:inline">소속:</span>
                      <span className="truncate">{report.teamName}</span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 min-w-0">
                      <Clock className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      <span className="text-[11px] sm:text-xs text-gray-500 truncate">
                        <span className="sm:hidden">{formatDateShort(new Date(report.submittedAt))}</span>
                        <span className="hidden sm:inline">{formatDateTime(new Date(report.submittedAt))}</span>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Report Detail - 전체 폭 */}
        <div>
          {selectedReport ? (
              <div className="bg-white rounded-xl shadow-lg p-8">
                {/* Report Header */}
                <div className="border-b-2 border-gray-200 pb-6 mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">주간 업무 보고</h2>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="font-semibold">작성자:</span> {selectedReport.userName}</div>
                    <div><span className="font-semibold">소속:</span> {selectedReport.teamName}</div>
                    <div><span className="font-semibold">보고 기간:</span> {formatDate(new Date(selectedReport.weekStart))} ~ {formatDate(new Date(selectedReport.weekEnd))}</div>
                    <div><span className="font-semibold">제출일:</span> {formatDateTimeWithSeconds(new Date(selectedReport.submittedAt))}</div>
                  </div>
                </div>

                {/* 금주 실적 비교: 좌=이전주 금주실적, 우=현재 금주실적 (레거시 content fallback 포함) */}
                <div className="mb-8 print:hidden">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* 좌측: 이전주 금주 실적 */}
                    <div className="rounded-xl border-2 border-gray-200 bg-gray-50/60 p-5">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-bold text-gray-700 flex items-center gap-2">
                          <span className="inline-block w-1.5 h-5 bg-gray-400 rounded-full" />
                          이전주 실적
                        </h3>
                        {prevWeekReport && (
                          <span className="text-xs text-gray-500">
                            {formatDate(new Date(prevWeekReport.weekStart))} ~ {formatDate(new Date(prevWeekReport.weekEnd))}
                          </span>
                        )}
                      </div>
                      {prevWeekReport ? (
                        (prevWeekReport.thisWeekContent || prevWeekReport.content) ? (
                          <div
                            className="prose ql-editor max-w-none text-gray-700"
                            dangerouslySetInnerHTML={{ __html: prevWeekReport.thisWeekContent || prevWeekReport.content }}
                          />
                        ) : (
                          <p className="text-sm text-gray-400">이전주 실적 내용이 없습니다.</p>
                        )
                      ) : (
                        <p className="text-sm text-gray-400">이전주에 제출된 보고서가 없습니다.</p>
                      )}
                    </div>

                    {/* 우측: 현재(선택주) 금주 실적 */}
                    <div className="rounded-xl border-2 border-orange-200 bg-orange-50/40 p-5">
                      <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="inline-block w-1.5 h-5 bg-orange-500 rounded-full" />
                        금주 실적
                      </h3>
                      {(selectedReport.thisWeekContent || selectedReport.nextWeekPlan) ? (
                        selectedReport.thisWeekContent ? (
                          <div
                            className="prose ql-editor max-w-none text-gray-700"
                            dangerouslySetInnerHTML={{ __html: selectedReport.thisWeekContent }}
                          />
                        ) : (
                          <p className="text-sm text-gray-400">작성된 금주 실적이 없습니다.</p>
                        )
                      ) : (
                        <div
                          className="prose ql-editor max-w-none text-gray-700"
                          dangerouslySetInnerHTML={{ __html: selectedReport.content }}
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* 차주 계획(좌) / 장비 이슈(우) */}
                <div className="mb-8 print:hidden">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* 좌측: 차주 계획 */}
                    <div className="rounded-xl border-2 border-blue-200 bg-blue-50/40 p-5">
                      <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="inline-block w-1.5 h-5 bg-blue-500 rounded-full" />
                        차주 계획
                      </h3>
                      {selectedReport.nextWeekPlan ? (
                        <div
                          className="prose ql-editor max-w-none text-gray-700"
                          dangerouslySetInnerHTML={{ __html: selectedReport.nextWeekPlan }}
                        />
                      ) : (
                        <p className="text-sm text-gray-400">작성된 차주 계획이 없습니다.</p>
                      )}
                    </div>

                    {/* 우측: 관련 장비 이슈 */}
                    <div className="rounded-xl border-2 border-gray-200 bg-white p-5">
                      <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <Link2 className="w-5 h-5 text-orange-600" />
                        관련 장비 이슈
                        {Array.isArray(selectedReport.linkedIssues) && selectedReport.linkedIssues.length > 0 && (
                          <span className="text-sm font-normal text-gray-500">({selectedReport.linkedIssues.length})</span>
                        )}
                      </h3>
                      {Array.isArray(selectedReport.linkedIssues) && selectedReport.linkedIssues.length > 0 ? (
                        <LinkedIssueBadges
                          issues={selectedReport.linkedIssues as LinkedIssue[]}
                          onNavigate={handleNavigateToIssue}
                        />
                      ) : (
                        <p className="text-sm text-gray-400">연결된 장비 이슈가 없습니다.</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Print-only Report Content - 인쇄 전용 2단 구조 (금주 실적/차주 계획, 레거시 content fallback 포함) */}
                <div className="hidden print:block mb-8 space-y-6">
                  {(selectedReport.thisWeekContent || selectedReport.nextWeekPlan) ? (
                    <>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">금주 실적</h3>
                        {selectedReport.thisWeekContent ? (
                          <div
                            className="prose ql-editor max-w-none text-gray-700"
                            dangerouslySetInnerHTML={{ __html: selectedReport.thisWeekContent }}
                          />
                        ) : (
                          <p className="text-sm text-gray-400">작성된 금주 실적이 없습니다.</p>
                        )}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">차주 계획</h3>
                        {selectedReport.nextWeekPlan ? (
                          <div
                            className="prose ql-editor max-w-none text-gray-700"
                            dangerouslySetInnerHTML={{ __html: selectedReport.nextWeekPlan }}
                          />
                        ) : (
                          <p className="text-sm text-gray-400">작성된 차주 계획이 없습니다.</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">보고 내용</h3>
                      <div
                        className="prose ql-editor max-w-none text-gray-700"
                        dangerouslySetInnerHTML={{ __html: selectedReport.content }}
                      />
                    </div>
                  )}
                </div>

                {/* Attachments Section */}
                {attachments.length > 0 && (
                  <div className="mb-8 pt-8 border-t-2 border-gray-200">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Paperclip className="w-6 h-6 text-orange-600" />
                      첨부파일 ({attachments.length})
                    </h3>
                    <div className="space-y-2">
                      {attachments.map((attachment: any) => (
                        <div
                          key={attachment._id}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-orange-300 transition-colors"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <Paperclip className="w-5 h-5 text-gray-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {attachment.fileName}
                              </p>
                              <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                                <span>{formatFileSize(attachment.fileSize)}</span>
                                <span>•</span>
                                <span>{new Date(attachment.uploadedAt).toLocaleDateString('ko-KR')}</span>
                                <span>•</span>
                                <span>{attachment.uploaderName}</span>
                              </div>
                            </div>
                          </div>
                          <a
                            href={attachment.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-3 p-2 bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 transition-colors flex-shrink-0"
                            title="다운로드"
                          >
                            <Download className="w-5 h-5" />
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Time Entries Summary */}
                <div className="mt-8 pt-8 border-t-2 border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Clock className="w-6 h-6 text-orange-600" />
                    주간 프로젝트 투입시간
                  </h3>

                  {(() => {
                    const projectTimeSummary = getProjectTimeSummary()
                    const hasTimeData = Object.keys(projectTimeSummary).length > 0

                    if (!hasTimeData) {
                      return (
                        <div className="text-center py-8 text-gray-500">
                          <p>해당 주간에 입력된 투입시간이 없습니다.</p>
                        </div>
                      )
                    }

                    return (
                      <>
                        {/* Activity Legend */}
                        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">활동코드 범례</h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {Object.entries(ACTIVITY_COLORS).map(([code, color]) => (
                              <div key={code} className="flex items-center gap-2">
                                <div 
                                  className="w-4 h-4 rounded"
                                  style={{ backgroundColor: color }}
                                />
                                <span className="text-sm text-gray-700">{code}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Project Time Table */}
                        <div className="space-y-4">
                          {Object.entries(projectTimeSummary).map(([projectName, activities]) => {
                            const totalHours = Object.values(activities).reduce((sum: number, hours: any) => sum + hours, 0)
                            return (
                              <div key={projectName} className="border border-gray-200 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="font-semibold text-gray-900">{projectName}</h4>
                                  <span className="text-sm font-medium text-orange-600">
                                    총 {totalHours.toFixed(1)}시간
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                  {Object.entries(activities).map(([activityCode, hours]: [string, any]) => (
                                    <div 
                                      key={activityCode}
                                      className="flex items-center justify-between px-3 py-2 rounded-lg"
                                      style={{ 
                                        backgroundColor: `${ACTIVITY_COLORS[activityCode as keyof typeof ACTIVITY_COLORS]}40`,
                                        borderLeft: `4px solid ${ACTIVITY_COLORS[activityCode as keyof typeof ACTIVITY_COLORS]}`
                                      }}
                                    >
                                      <span className="text-sm text-gray-700">{activityCode}</span>
                                      <span className="text-sm font-semibold text-gray-900">{hours.toFixed(1)}h</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </>
                    )
                  })()}
                </div>
              </div>
          ) : (
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-center py-16 text-gray-500">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-lg">좌측 목록에서 보고서를 선택하세요</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function getMonday(date: Date): Date {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1)
  return new Date(d.setDate(diff))
}

function getWeekEnd(startDate: Date): Date {
  const end = new Date(startDate)
  end.setDate(end.getDate() + 6)
  return end
}

function formatDate(date: Date): string {
  return date.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).replace(/\. /g, ".").replace(".", "년 ").replace(".", "월 ").replace(".", "일")
}

function formatDateTime(date: Date): string {
  return date.toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).replace(/\. /g, ".").replace(".", "년 ").replace(".", "월 ").replace(".", "일 ")
}

// 모바일 2열 카드용 축약 날짜 (예: 09.03)
function formatDateShort(date: Date): string {
  return date
    .toLocaleDateString("ko-KR", { month: "2-digit", day: "2-digit" })
    .replace(/\s/g, "")
    .replace(/\.$/, "")
}

function formatDateTimeWithSeconds(date: Date): string {
  return date.toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  }).replace(/\. /g, ".").replace(".", "년 ").replace(".", "월 ").replace(".", "일 ")
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}
